import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import {
  getCarrierCosts, putCarrierTariffs,
  type BillingIncrement, type CallDirection, type CarrierCosts, type CarrierTariffRow, type CarrierTariffs, type CostCarrier,
} from '@/api/endpoints/carriers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VFStatCard } from '@/components/vf/StatCard';
import { ErrorLine } from '@/components/backoffice/platform/PlatformBits';
import { currentLocale } from '@/i18n/locale';

/**
 * SU-3 (W7): Asterisk and Zadarma in the « Coûts » tab of TwilioConfig.
 * Twilio reports its own usage; these two do not, so the figures are an
 * ESTIMATE (ENG-18), said in the title, on every figure and in the table:
 * the duration of the answered calls of the month priced with the tariff
 * table (GET /admin/platform/carrier-costs). The table is edited below
 * (PUT /admin/platform/carrier-tariffs): one rate per carrier, direction and
 * destination prefix, billed by the second or by the started minute. Editing
 * is reversible (the previous values can be typed back), so no confirmation.
 * Defaults are stated in docs/runbooks/refonte-ui-rollout.md (DX-12).
 */

const TH = 'h-9 px-3 text-[11px] font-semibold uppercase tracking-wide';
const PREFIX = /^(\+\d{1,6})?$/;
const CARRIERS: CostCarrier[] = ['asterisk', 'zadarma'];
const DIRECTIONS: CallDirection[] = ['outbound', 'inbound'];
const INCREMENTS: BillingIncrement[] = ['per_second', 'per_minute'];
const SELECT = 'h-10 w-full rounded-md border bg-transparent px-2 text-sm';

/**
 * REV-W7-F11: while editing, prices stay the text typed (an emptied field is
 * not 0); they become numbers only when saved.
 */
type DraftRow = Omit<CarrierTariffRow, 'per_minute'> & { per_minute: number | string };
export type TariffDraft = Omit<CarrierTariffs, 'zadarma_number_monthly' | 'rates'> & { zadarma_number_monthly: number | string; rates: DraftRow[] };

/** A typed amount as a number; an empty or unreadable field is NaN (a problem), never 0. */
const amountOf = (v: number | string): number => (typeof v === 'number' ? v : v.trim() === '' ? Number.NaN : Number(v.trim().replace(',', '.')));

const toDraft = (t: CarrierTariffs): TariffDraft => ({
  ...structuredClone(t),
  zadarma_number_monthly: String(t.zadarma_number_monthly),
  rates: t.rates.map((r) => ({ ...r, per_minute: String(r.per_minute) })),
});
const fromDraft = (d: TariffDraft): CarrierTariffs => ({
  ...d,
  zadarma_number_monthly: amountOf(d.zadarma_number_monthly),
  rates: d.rates.map((r) => ({ ...r, label: r.label.trim(), per_minute: amountOf(r.per_minute) })),
});

/** Problems of a draft, by row index ('' = the whole table); empty when it can be saved. */
export function tariffProblems(draft: TariffDraft): Record<string, string> {
  const out: Record<string, string> = {};
  if (!/^[A-Z]{3}$/.test(draft.currency)) out.currency = 'currency';
  if (!(amountOf(draft.zadarma_number_monthly) >= 0)) out.monthly = 'amount';
  const seen = new Set<string>();
  draft.rates.forEach((r, i) => {
    const key = `${r.carrier}|${r.direction}|${r.prefix}`;
    const price = amountOf(r.per_minute);
    if (!PREFIX.test(r.prefix)) out[i] = 'prefix';
    else if (!(price >= 0) || price > 100) out[i] = 'amount';
    else if (seen.has(key)) out[i] = 'duplicate';
    seen.add(key);
  });
  return out;
}

function TariffEditor({ tariffs, onDone }: { tariffs: CarrierTariffs; onDone: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const [draft, setDraft] = useState<TariffDraft>(() => toDraft(tariffs));
  const [tried, setTried] = useState(false);
  const save = useMutation({
    mutationFn: (v: CarrierTariffs) => putCarrierTariffs(v),
    onSuccess: () => {
      toast.success(t('admin:twilioConfig.carrierCosts.tariffs.saved'));
      void qc.invalidateQueries({ queryKey: ['carrier-costs'] });
      onDone();
    },
  });
  const problems = tariffProblems(draft);
  const setRow = (i: number, patch: Partial<DraftRow>) =>
    setDraft((d) => ({ ...d, rates: d.rates.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const submit = () => {
    setTried(true);
    if (Object.keys(problems).length === 0) save.mutate(fromDraft(draft));
  };
  const problemText = (k: string) => (tried && problems[k] ? t(`admin:twilioConfig.carrierCosts.tariffs.problems.${problems[k]}`) : null);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="tariff-currency">{t('admin:twilioConfig.carrierCosts.tariffs.currency')}</Label>
          <Input id="tariff-currency" value={draft.currency} maxLength={3} onChange={(e) => setDraft((d) => ({ ...d, currency: e.target.value.toUpperCase() }))} aria-invalid={!!problemText('currency') || undefined} />
          {problemText('currency') && <p role="alert" className="text-xs" style={{ color: 'var(--vf-fill-danger)' }}>{problemText('currency')}</p>}
        </div>
        <div className="space-y-1">
          <Label htmlFor="tariff-monthly">{t('admin:twilioConfig.carrierCosts.tariffs.numberMonthly')}</Label>
          <Input id="tariff-monthly" type="number" min={0} step="0.01" value={String(draft.zadarma_number_monthly)} onChange={(e) => setDraft((d) => ({ ...d, zadarma_number_monthly: e.target.value }))} aria-invalid={!!problemText('monthly') || undefined} />
          {problemText('monthly') && <p role="alert" className="text-xs" style={{ color: 'var(--vf-fill-danger)' }}>{problemText('monthly')}</p>}
        </div>
      </div>
      <div className="overflow-x-auto rounded-vf border" style={{ borderColor: 'var(--vf-border)' }}>
        <table className="w-full min-w-[52rem] text-[13px]">
          <caption className="sr-only">{t('admin:twilioConfig.carrierCosts.tariffs.title')}</caption>
          <thead style={{ background: 'var(--vf-surface-1)', color: 'var(--vf-text-3)' }}>
            <tr>
              <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.carrier')}</th>
              <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.direction')}</th>
              <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.prefix')}</th>
              <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.label')}</th>
              <th scope="col" className={`${TH} text-right`}>{t('admin:twilioConfig.carrierCosts.columns.perMinute', { currency: draft.currency })}</th>
              <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.increment')}</th>
              <th scope="col" className={TH}><span className="sr-only">{t('common:actions.delete')}</span></th>
            </tr>
          </thead>
          <tbody>
            {draft.rates.map((r, i) => {
              const n = i + 1;
              // QA-W7-8: the message sits on the field that is wrong: the price
              // for a bad amount, the prefix for a bad or duplicate prefix.
              const rowProblem = problemText(String(i));
              const onPrice = rowProblem && problems[String(i)] === 'amount' ? rowProblem : null;
              const onPrefix = rowProblem && !onPrice ? rowProblem : null;
              return (
                <tr key={i} className="border-t align-top" style={{ borderColor: 'var(--vf-border)' }}>
                  <td className="px-2 py-1.5">
                    <select className={SELECT} style={{ borderColor: 'var(--vf-border)' }} aria-label={t('admin:twilioConfig.carrierCosts.tariffs.rowCarrier', { n })} value={r.carrier} onChange={(e) => setRow(i, { carrier: e.target.value as CostCarrier })}>
                      {CARRIERS.map((c) => <option key={c} value={c}>{t(`admin:twilioConfig.carrierCosts.carriers.${c}`)}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <select className={SELECT} style={{ borderColor: 'var(--vf-border)' }} aria-label={t('admin:twilioConfig.carrierCosts.tariffs.rowDirection', { n })} value={r.direction} onChange={(e) => setRow(i, { direction: e.target.value as CallDirection })}>
                      {DIRECTIONS.map((d) => <option key={d} value={d}>{t(`admin:twilioConfig.carrierCosts.directions.${d}`)}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <Input aria-label={t('admin:twilioConfig.carrierCosts.tariffs.rowPrefix', { n })} placeholder={t('admin:twilioConfig.carrierCosts.tariffs.anyPrefix')} value={r.prefix} onChange={(e) => setRow(i, { prefix: e.target.value.trim() })} className="font-mono" aria-invalid={!!onPrefix || undefined} aria-describedby={onPrefix ? `tariff-row-${i}-prefix-error` : undefined} />
                    {onPrefix && <p id={`tariff-row-${i}-prefix-error`} role="alert" className="pt-1 text-xs" style={{ color: 'var(--vf-fill-danger)' }}>{onPrefix}</p>}
                  </td>
                  <td className="px-2 py-1.5">
                    <Input aria-label={t('admin:twilioConfig.carrierCosts.tariffs.rowLabel', { n })} value={r.label} maxLength={60} onChange={(e) => setRow(i, { label: e.target.value })} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input aria-label={t('admin:twilioConfig.carrierCosts.tariffs.rowPrice', { n })} type="number" min={0} step="0.001" value={String(r.per_minute)} onChange={(e) => setRow(i, { per_minute: e.target.value })} className="text-right tabular-nums" aria-invalid={!!onPrice || undefined} aria-describedby={onPrice ? `tariff-row-${i}-price-error` : undefined} />
                    {onPrice && <p id={`tariff-row-${i}-price-error`} role="alert" className="pt-1 text-xs" style={{ color: 'var(--vf-fill-danger)' }}>{onPrice}</p>}
                  </td>
                  <td className="px-2 py-1.5">
                    <select className={SELECT} style={{ borderColor: 'var(--vf-border)' }} aria-label={t('admin:twilioConfig.carrierCosts.tariffs.rowIncrement', { n })} value={r.increment} onChange={(e) => setRow(i, { increment: e.target.value as BillingIncrement })}>
                      {INCREMENTS.map((x) => <option key={x} value={x}>{t(`admin:twilioConfig.carrierCosts.increments.${x}`)}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Button type="button" variant="ghost" size="icon" className="h-11 w-11" aria-label={t('admin:twilioConfig.carrierCosts.tariffs.removeRow', { n })} onClick={() => setDraft((d) => ({ ...d, rates: d.rates.filter((_, j) => j !== i) }))}>
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" className="gap-2" onClick={() => setDraft((d) => ({ ...d, rates: [...d.rates, { carrier: 'asterisk', direction: 'outbound', prefix: '', label: '', per_minute: '', increment: 'per_second' }] }))}>
          <Plus className="h-4 w-4" aria-hidden />{t('admin:twilioConfig.carrierCosts.tariffs.addRow')}
        </Button>
        <span className="flex-1" />
        <Button type="button" variant="ghost" onClick={onDone}>{t('common:actions.cancel')}</Button>
        <Button type="button" onClick={submit} disabled={save.isPending}>{save.isPending ? t('admin:twilioConfig.carrierCosts.tariffs.saving') : t('admin:twilioConfig.carrierCosts.tariffs.save')}</Button>
      </div>
      {save.isError && <ErrorLine message={t('admin:twilioConfig.carrierCosts.tariffs.saveFailed')} error={save.error} />}
    </div>
  );
}

export function CarrierCostsSection({ month }: { month: string }) {
  const { t } = useTranslation(['admin', 'common']);
  const [editing, setEditing] = useState(false);
  const costs = useQuery<CarrierCosts>({
    queryKey: ['carrier-costs', month],
    queryFn: ({ signal }) => getCarrierCosts(month, signal),
    retry: false,
  });
  const c = costs.data;
  const state = costs.isError ? 'error' : c ? 'ready' : 'loading';
  const money = (n: number) => n.toLocaleString(currentLocale(), { style: 'currency', currency: c?.currency || 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const num = (n: number, digits = 1) => n.toLocaleString(currentLocale(), { maximumFractionDigits: digits });
  const est = t('admin:twilioConfig.carrierCosts.estimate');

  return (
    <section aria-labelledby="carrier-costs-title" className="space-y-3 border-t pt-5" style={{ borderColor: 'var(--vf-border)' }}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="carrier-costs-title" className="text-base font-semibold">{t('admin:twilioConfig.carrierCosts.title')}</h2>
        <span className="rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide" style={{ background: 'var(--vf-surface-2)', color: 'var(--vf-text-2)' }}>{est}</span>
      </div>
      <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:twilioConfig.carrierCosts.desc')}</p>

      {costs.isError && <ErrorLine message={t('admin:twilioConfig.carrierCosts.loadFailed')} error={costs.error} onRetry={() => void costs.refetch()} />}
      {c?.tariffs_source === 'invalid_setting' && <p role="status" className="text-sm" style={{ color: 'var(--vf-fill-warning, var(--vf-text-1))' }}>{t('admin:twilioConfig.carrierCosts.invalidSetting')}</p>}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <VFStatCard label={`${t('admin:twilioConfig.carrierCosts.carriers.asterisk')} (${est})`} state={state} value={c ? money(c.totals.asterisk.cost) : undefined} hint={c ? t('admin:twilioConfig.carrierCosts.callsMinutes', { count: c.totals.asterisk.calls, minutes: num(c.totals.asterisk.seconds / 60) }) : undefined} />
        <VFStatCard label={`${t('admin:twilioConfig.carrierCosts.carriers.zadarma')} (${est})`} state={state} value={c ? money(c.totals.zadarma.cost) : undefined} hint={c ? t('admin:twilioConfig.carrierCosts.zadarmaHint', { count: c.totals.zadarma.calls, numbers: c.zadarma_numbers.count, fees: money(c.zadarma_numbers.monthly_cost) }) : undefined} />
        <VFStatCard label={`${t('admin:twilioConfig.carrierCosts.total')} (${est})`} state={state} value={c ? money(c.total_cost) : undefined} />
      </div>

      {c && c.lines.length === 0 && <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:twilioConfig.carrierCosts.empty')}</p>}
      {c && c.lines.length > 0 && (
        <div className="overflow-x-auto rounded-vf border" style={{ borderColor: 'var(--vf-border)' }}>
          <table className="w-full text-[13px]">
            <caption className="sr-only">{t('admin:twilioConfig.carrierCosts.byDestination')}</caption>
            <thead style={{ background: 'var(--vf-surface-1)', color: 'var(--vf-text-3)' }}>
              <tr>
                <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.carrier')}</th>
                <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.direction')}</th>
                <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.destination')}</th>
                <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.increment')}</th>
                <th scope="col" className={`${TH} text-right`}>{t('admin:twilioConfig.carrierCosts.columns.perMinute', { currency: c.currency })}</th>
                <th scope="col" className={`${TH} text-right`}>{t('admin:twilioConfig.usage.tableCalls')}</th>
                <th scope="col" className={`${TH} text-right`}>{t('admin:twilioConfig.carrierCosts.columns.billedMinutes')}</th>
                <th scope="col" className={`${TH} text-right`}>{t('admin:twilioConfig.carrierCosts.columns.cost')}</th>
              </tr>
            </thead>
            <tbody>
              {c.lines.map((l) => (
                <tr key={`${l.carrier}|${l.direction}|${l.priced ? l.prefix : '?'}`} className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                  <td className="h-10 px-3">{t(`admin:twilioConfig.carrierCosts.carriers.${l.carrier}`)}</td>
                  <td className="h-10 px-3">{t(`admin:twilioConfig.carrierCosts.directions.${l.direction}`)}</td>
                  <td className="h-10 px-3">
                    {l.priced ? <>{l.label || '—'} <span className="font-mono text-xs" style={{ color: 'var(--vf-text-3)' }}>{l.prefix || t('admin:twilioConfig.carrierCosts.tariffs.anyPrefix')}</span></> : t('admin:twilioConfig.carrierCosts.noRate')}
                  </td>
                  <td className="h-10 px-3">{l.priced ? t(`admin:twilioConfig.carrierCosts.increments.${l.increment}`) : '—'}</td>
                  <td className="h-10 px-3 text-right tabular-nums">{l.priced ? num(l.per_minute, 4) : '—'}</td>
                  <td className="h-10 px-3 text-right tabular-nums">{l.calls}</td>
                  <td className="h-10 px-3 text-right tabular-nums">{l.priced ? num(l.billed_minutes, 2) : '—'}</td>
                  <td className="h-10 px-3 text-right tabular-nums">{l.priced ? money(l.cost) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {c && c.unpriced_calls > 0 && <p className="text-sm" role="status">{t('admin:twilioConfig.carrierCosts.unpriced', { count: c.unpriced_calls })}</p>}

      <section aria-labelledby="carrier-tariffs-title" className="space-y-2 rounded-vf border p-4" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id="carrier-tariffs-title" className="text-sm font-semibold">{t('admin:twilioConfig.carrierCosts.tariffs.title')}</h3>
          {c && !editing && <Button type="button" variant="outline" onClick={() => setEditing(true)}>{t('admin:twilioConfig.carrierCosts.tariffs.edit')}</Button>}
        </div>
        {c && (
          <p className="text-xs" style={{ color: 'var(--vf-text-2)' }}>
            {c.tariffs_source === 'setting' && c.tariffs_updated_at
              ? t('admin:twilioConfig.carrierCosts.tariffs.fromSetting', { date: new Date(c.tariffs_updated_at).toLocaleDateString(currentLocale()) })
              : t('admin:twilioConfig.carrierCosts.tariffs.fromDefaults')}
          </p>
        )}
        {c && editing && <TariffEditor tariffs={c.tariffs} onDone={() => setEditing(false)} />}
        {c && !editing && (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <caption className="sr-only">{t('admin:twilioConfig.carrierCosts.tariffs.title')}</caption>
              <thead style={{ color: 'var(--vf-text-3)' }}>
                <tr>
                  <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.carrier')}</th>
                  <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.direction')}</th>
                  <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.destination')}</th>
                  <th scope="col" className={`${TH} text-right`}>{t('admin:twilioConfig.carrierCosts.columns.perMinute', { currency: c.tariffs.currency })}</th>
                  <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.carrierCosts.columns.increment')}</th>
                </tr>
              </thead>
              <tbody>
                {c.tariffs.rates.map((r) => (
                  <tr key={`${r.carrier}|${r.direction}|${r.prefix}`} className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                    <td className="h-9 px-3">{t(`admin:twilioConfig.carrierCosts.carriers.${r.carrier}`)}</td>
                    <td className="h-9 px-3">{t(`admin:twilioConfig.carrierCosts.directions.${r.direction}`)}</td>
                    <td className="h-9 px-3">{r.label || '—'} <span className="font-mono text-xs" style={{ color: 'var(--vf-text-3)' }}>{r.prefix || t('admin:twilioConfig.carrierCosts.tariffs.anyPrefix')}</span></td>
                    <td className="h-9 px-3 text-right tabular-nums">{num(r.per_minute, 4)}</td>
                    <td className="h-9 px-3">{t(`admin:twilioConfig.carrierCosts.increments.${r.increment}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="pt-2 text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('admin:twilioConfig.carrierCosts.tariffs.numberMonthlyValue', { amount: money(c.tariffs.zadarma_number_monthly) })}</p>
          </div>
        )}
      </section>
    </section>
  );
}
